import {ErrorHandler, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Params, Router, UrlTree} from '@angular/router';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {MpopFeatureModel} from './model/mpop-feature.model';
import {UserService} from './service/user.service';

interface IpopFeatureState
{
  enhancementEnabled: boolean;
  explicitSaveEnabled: boolean;
}

export const ipopConfigurationRoutePaths = {
  dispatcher: 'settings/ipop-configuration',
  enhanced: 'settings/ipop-rule-workflow-enhancement',
  explicitSave: 'settings/ipop-explicit-save',
  original: 'settings/ipop'
};

const accountIdPattern = /^[1-9]\d*$/;
const enhancedFeatureName = 'ipopRuleWorkflowEnhancement';
const explicitSaveFeatureName = 'explicitSaveOfferRules';
const offerSetRulesTab = 'offerSetRules';
const privateOffersTab = 'privateOffers';
const tabQueryParameter = 'tab';
const vdpOffersTab = 'vdpOffers';
const supportedTabs = [privateOffersTab, vdpOffersTab, offerSetRulesTab, 'siteWideUnlock'];

@Injectable({
  providedIn: 'root'
})
export class IpopConfigurationRouteService
{
  constructor(
    private userService: UserService,
    private router: Router,
    private errorHandler: ErrorHandler
  )
  {
  }

  /** Resolves the stable dispatcher to one concrete IPOP route. */
  resolveDispatcherRoute(route: ActivatedRouteSnapshot): Observable<UrlTree>
  {
    const routeRequest = this.loadFeatureState().pipe(
      map((featureState) => this.buildPreferredRoute(featureState, route)),
      catchError((error: unknown) => this.buildFailureRoute(error, route))
    );

    return routeRequest;
  }

  /** Allows enhanced access or resolves the approved concrete fallback. */
  resolveEnhancedRoute(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree>
  {
    const routeRequest = this.loadFeatureState().pipe(
      map((featureState) => this.buildEnhancedRouteDecision(featureState, route)),
      catchError((error: unknown) => this.buildFailureRoute(error, route))
    );

    return routeRequest;
  }

  /** Loads both route flags from one authenticated feature response. */
  private loadFeatureState(): Observable<IpopFeatureState>
  {
    const featureRequest = this.userService.getAllMpopFeatures().pipe(
      map((features) => this.buildFeatureState(features))
    );

    return featureRequest;
  }

  /** Interprets only exact enabled feature entries. */
  private buildFeatureState(features: MpopFeatureModel[]): IpopFeatureState
  {
    const featureState: IpopFeatureState = {
      enhancementEnabled: this.isFeatureEnabled(features, enhancedFeatureName),
      explicitSaveEnabled: this.isFeatureEnabled(features, explicitSaveFeatureName)
    };

    return featureState;
  }

  /** Checks an authoritative response for one enabled named feature. */
  private isFeatureEnabled(features: MpopFeatureModel[], featureName: string): boolean
  {
    const isEnabled = features.some((feature) => {

      return feature.name === featureName && feature.enabled === true;
    });

    return isEnabled;
  }

  /** Applies enhancement, explicit-save, then original precedence. */
  private buildPreferredRoute(featureState: IpopFeatureState, route: ActivatedRouteSnapshot): UrlTree
  {
    let preferredRoute = ipopConfigurationRoutePaths.original;
    const hasValidAccountId = this.hasValidAccountId(route);

    if (hasValidAccountId && featureState.explicitSaveEnabled) {
      preferredRoute = ipopConfigurationRoutePaths.explicitSave;
    }
    if (hasValidAccountId && featureState.enhancementEnabled) {
      preferredRoute = ipopConfigurationRoutePaths.enhanced;
    }

    return this.buildConcreteRoute(preferredRoute, route);
  }

  /** Allows enhanced access only while its authoritative flag is enabled. */
  private buildEnhancedRouteDecision(
    featureState: IpopFeatureState,
    route: ActivatedRouteSnapshot
  ): boolean | UrlTree
  {
    const hasValidAccountId = this.hasValidAccountId(route);
    let routeDecision: boolean | UrlTree = this.buildConcreteRoute(ipopConfigurationRoutePaths.original, route);

    if (hasValidAccountId) {
      routeDecision = this.buildFallbackRoute(featureState, route);
    }
    if (hasValidAccountId && featureState.enhancementEnabled) {
      routeDecision = true;
    }

    return routeDecision;
  }

  /** Selects explicit-save or original after enhancement is unavailable. */
  private buildFallbackRoute(featureState: IpopFeatureState, route: ActivatedRouteSnapshot): UrlTree
  {
    let fallbackRoute = ipopConfigurationRoutePaths.original;

    if (featureState.explicitSaveEnabled) {
      fallbackRoute = ipopConfigurationRoutePaths.explicitSave;
    }

    return this.buildConcreteRoute(fallbackRoute, route);
  }

  /** Reports feature-load failure and fails closed to original IPOP. */
  private buildFailureRoute(error: unknown, route: ActivatedRouteSnapshot): Observable<UrlTree>
  {
    this.errorHandler.handleError(error);
    const failureRoute = of(this.buildConcreteRoute(ipopConfigurationRoutePaths.original, route));

    return failureRoute;
  }

  /** Builds a fresh concrete destination with approved route state only. */
  private buildConcreteRoute(routePath: string, route: ActivatedRouteSnapshot): UrlTree
  {
    const accountId = this.getValidatedAccountId(route);
    const absoluteRoutePath = `/${routePath}`;
    const concreteRoute = this.router.createUrlTree(
      [absoluteRoutePath, accountId],
      {queryParams: this.buildSupportedQueryParams(route)}
    );

    return concreteRoute;
  }

  /** Preserves a valid account route value exactly. */
  private getValidatedAccountId(route: ActivatedRouteSnapshot): string
  {
    const routeAccountId = route.paramMap.get('accountId');
    let accountId = '';

    if (routeAccountId !== null && accountIdPattern.test(routeAccountId)) {
      const parsedAccountId = Number(routeAccountId);
      if (Number.isSafeInteger(parsedAccountId)) {
        accountId = routeAccountId;
      }
    }

    return accountId;
  }

  /** Checks whether route state contains a usable account identifier. */
  private hasValidAccountId(route: ActivatedRouteSnapshot): boolean
  {
    const isValid = this.getValidatedAccountId(route) !== '';

    return isValid;
  }

  /** Copies only the supported IPOP tab query parameter. */
  private buildSupportedQueryParams(route: ActivatedRouteSnapshot): Params
  {
    const queryParams: Params = {};
    const requestedTab = route.queryParamMap.get(tabQueryParameter);

    if (requestedTab !== null && supportedTabs.includes(requestedTab)) {
      queryParams[tabQueryParameter] = requestedTab;
    }

    return queryParams;
  }
}
